import { http, HttpResponse } from "msw";

import {
  NOTIFICATION_ENUMS_RESPONSE,
  createNotification,
  deleteNotification,
  editNotification,
  getBrandName,
  getLogo,
  listNotifications,
  setBrandName,
  setLogo,
  type CreateNotificationBody,
  type EditNotificationBody,
} from "../data/settings";

/**
 * msw handlers for the `/settings` section endpoints (FRO-363). Registered once
 * in the router registry (adr/0003): the legacy tech-ui view and the migrated
 * connect feature both execute in the router document and share this single
 * worker + the one in-memory fixture, so a create in either implementation is
 * read back by the same list re-fetch. Wildcard prefixes match regardless of
 * the `/public` base each app builds against.
 *
 * Shapes are the captured wire truth (research/04 §5): the list is a BARE JSON
 * array, create returns `{ success: true }`, and delete returns an empty body.
 */

/** Recipient that forces the create endpoint to return a 500 (failure-path spec). */
const FAILURE_SENTINEL_EMAIL = "fail@example.com";

/**
 * Display name that forces the brand-name PUT to return a 500, so the
 * display-name-save failure spec can assert the error toast + unchanged value
 * without a per-test handler override.
 */
const FAILURE_SENTINEL_DISPLAY_NAME = "Trigger Save Failure";

/**
 * Uploaded file name that forces the logo POST to return a 500, so the failed
 * logo-upload spec can assert the error toast + retained current logo.
 */
const FAILURE_SENTINEL_LOGO_FILENAME = "fail-logo.png";

const settingsHandlers = [
  // The create/edit selects fetch notification enums from `/enums/` with
  // `?notificationType=…&notificationTriggerType=…`. The tech-ui shell also
  // hits a bare `/enums/` for its own bootstrap data (see ./tech-ui.ts), so this
  // handler only answers the settings variant and otherwise falls through to
  // that handler (registered after this one).
  http.get("*/enums/", ({ request }) => {
    const url = new URL(request.url);
    if (
      url.searchParams.has("notificationType") ||
      url.searchParams.has("notificationTriggerType")
    ) {
      return HttpResponse.json(NOTIFICATION_ENUMS_RESPONSE);
    }
    return undefined;
  }),

  // Notification list — a bare JSON array, keyed by operatorId in the path.
  http.get("*/admin/notification-settings/:operatorId", () =>
    HttpResponse.json(listNotifications())
  ),

  // Create — appends to the fixture; the view re-fetches the list afterwards.
  // A sentinel recipient forces a server failure so the failure-path spec can
  // assert the error toast + unchanged list (legacy already toasts create
  // failures), without any per-test handler override.
  http.post(
    "*/admin/notification-settings/:operatorId",
    async ({ request, params }) => {
      const body = (await request.json()) as CreateNotificationBody;
      if (body.address === FAILURE_SENTINEL_EMAIL) {
        return new HttpResponse(null, { status: 500 });
      }
      createNotification(Number(params.operatorId), body);
      return HttpResponse.json({ success: true });
    }
  ),

  // Edit — patches the fixture row in place (path segment is the notification id).
  http.put("*/admin/notification-settings/:id", async ({ request, params }) => {
    const body = (await request.json()) as Omit<EditNotificationBody, "id">;
    editNotification({ ...body, id: Number(params.id) });
    return HttpResponse.json({ success: true });
  }),

  // Delete — removes the row; 200 with an empty body (research/04 §5).
  http.delete("*/admin/notification-settings/:id", ({ params }) => {
    deleteNotification(Number(params.id));
    return new HttpResponse(null, { status: 200 });
  }),

  // Brand name — `{ displayName }` on the wire (research/04 §5).
  http.get("*/admin/brand-name", () => HttpResponse.json(getBrandName())),

  http.put("*/admin/brand-name", async ({ request }) => {
    const body = (await request.json()) as { displayName: string | null };
    // A sentinel value forces a server failure so the save-failure spec can
    // assert the error toast + unchanged value; the fixture is left untouched.
    if (body.displayName === FAILURE_SENTINEL_DISPLAY_NAME) {
      return new HttpResponse(null, { status: 500 });
    }
    return HttpResponse.json(setBrandName(body.displayName));
  }),

  // Logo — `{ logo, s3Url }` (display URL = `s3Url + logo`); empty by default.
  http.get("*/admin/logo", () => HttpResponse.json(getLogo())),

  // Logo upload — multipart `FormData`, field `logo`. The view re-fetches the
  // logo afterwards, so the exact response body is not read; seed a value so a
  // subsequent GET reflects the upload. A sentinel file name forces a server
  // failure (failed-upload spec) without touching the current logo.
  http.post("*/admin/logo/:operatorId", async ({ request }) => {
    const formData = await request.formData();
    const file = formData.get("logo");
    if (file instanceof File && file.name === FAILURE_SENTINEL_LOGO_FILENAME) {
      return new HttpResponse(null, { status: 500 });
    }
    setLogo({ logo: "e2e-uploaded-logo.png", s3Url: "https://logos.example/" });
    return HttpResponse.json({ success: true });
  }),
];

export { settingsHandlers };
